import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../ui/cn';
import { CtaLink } from '../ui';

export type FeaturedInsight = { id: string; title: string; category: string; image?: string; to: string };

const BREAKPOINT_PAD = 'pl-6 lg:pl-[max(1.5rem,calc((100vw-72rem)/2))]';

/**
 * En-tête du blog: titre à gauche, article en vedette à droite (carte blanche en superposition),
 * onglets avec trait de progression qui sert de minuteur, et pause (comme le hero de l'accueil).
 */
export default function InsightsHero({ items }: { items: FeaturedInsight[] }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = items.length;
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const next = useCallback(() => setActive((n) => (n + 1) % Math.max(count, 1)), [count]);
  const item = items[Math.min(active, count - 1)];

  return (
    <section className={cn('bg-white', paused && 'hero-paused')}>
      <div className="grid lg:grid-cols-[40%_1fr]">
        <div className={cn('flex flex-col justify-center py-14 pr-6 lg:py-24', BREAKPOINT_PAD)}>
          <div className="relative">
            <span aria-hidden className="absolute right-full top-[2.6rem] mr-8 hidden h-[0.15rem] w-24 bg-brand lg:block lg:-translate-x-0" />
            <h1 className="font-display text-[3.5rem] font-bold normal-case leading-none tracking-normal text-black md:text-[5.5rem]">Actualités</h1>
            <p className="mt-6 max-w-sm text-[1.375rem] font-light leading-[2.4rem] text-ink">
              Des actualités sur nos projets, nos équipes, nos tendances et notre expertise du chantier.
            </p>
          </div>
        </div>

        <div className="relative">
          {item && (
            <>
              <div className="relative h-[20rem] overflow-hidden bg-secondary-200 md:h-[32rem]">
                {item.image && <img key={item.id} src={item.image} alt="" className="absolute inset-0 h-full w-full animate-fade-in object-cover" />}
              </div>

              <div key={item.id} className="relative -mt-12 mr-6 bg-white p-8 shadow-sm md:absolute md:right-0 md:top-16 md:mr-0 md:mt-0 md:w-[min(34rem,90%)] md:p-10">
                <div className="spot-text-0 text-xs font-normal uppercase tracking-[0.0625rem] text-ink">{item.category}</div>
                <h2 className="spot-text-0 mt-8 font-sans text-[1.75rem] font-bold normal-case leading-tight tracking-normal text-black md:text-[2.25rem]">{item.title}</h2>
                <div className="spot-text-1 mt-8"><CtaLink to={item.to}>Lire l'actualité</CtaLink></div>
              </div>

              {count > 1 && (
                <div className="mt-8 flex items-center justify-between gap-6 pb-6 pr-6 md:mt-10 md:pb-10 md:pr-16">
                  <ol className="flex flex-wrap gap-x-10 gap-y-3">
                    {items.map((it, n) => (
                      <li key={it.id} style={{ ['--i' as string]: n }}>
                        <button
                          onClick={() => setActive(n)}
                          aria-current={n === active}
                          className={cn('relative block overflow-hidden pb-2 font-display text-lg font-normal transition-colors duration-[250ms]', n === active ? 'text-black' : 'text-muted hover:text-black')}
                        >
                          <span
                            key={n === active ? `bar-${active}` : 'bar'}
                            data-active={n === active}
                            className="hero-tab-bar !bottom-0 !top-auto"
                            onAnimationEnd={(e) => { if (n === active && e.animationName === 'hero-bar') next(); }}
                          />
                          <span className="block max-w-[14rem] truncate">{it.title}</span>
                        </button>
                      </li>
                    ))}
                  </ol>
                  <button
                    aria-label={paused ? 'Reprendre le carrousel' : 'Mettre le carrousel en pause'}
                    onClick={() => setPaused((p) => !p)}
                    className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-[0.13rem] border-charcoal text-charcoal transition-colors duration-[250ms] hover:border-brand-dark hover:bg-brand-dark hover:text-white"
                  >
                    <svg aria-hidden viewBox="0 0 42 42" className="pointer-events-none absolute left-1/2 top-1/2 h-[2.625rem] w-[2.625rem] -translate-x-1/2 -translate-y-1/2 -rotate-90" fill="none">
                      <circle key={`ring-${active}`} data-active className="hero-ring" cx="21" cy="21" r="19" pathLength={1} stroke="var(--color-brand)" strokeWidth="2" />
                    </svg>
                    {paused ? <svg viewBox="0 0 10 12" className="h-3 w-3 pl-0.5" fill="currentColor"><path d="M1 0l9 6-9 6z" /></svg> : <svg viewBox="0 0 10 12" className="h-3 w-3" fill="currentColor"><path d="M0 0h3v12H0zM7 0h3v12H7z" /></svg>}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
